import store from 'config/store'
import { chain, flatten } from 'ramda'
import {
  ConditionalSkillEvaluationPayload,
  ConditionalSkillTargetingResult,
  EvaluateCampaignTargetingConditionsArgs,
  EvaluatedTargetConditionSetItem,
  EvaluateWidgetDisplayConditionsArgs,
  MatchedRoutingRule,
  OnTargetingConditionsMatchedArgs,
  RoutingTargetingResult,
  TargetingAuditLog, TargetingAuditLogCondition,
  TargetingAuditLogConditionSet, TargetingCondition,
  TargetingEvaluationOutcome,
  TargetingEvaluationResult,
  TargetingEvaluationStatus
} from 'types/targeting'
import { uuid } from 'utils'
import { broadcast } from 'utils/message'

import { setHasExitIntent } from '../actions'
import { evaluateTargetingConditions } from '../api'
import { generateTargetingContext } from '../index'
import { AddEvaluationResultToAuditLog, SaveAuditLog } from '../logging'
import { getTargetingEvaluationMetadata } from '../utils'

const asyncTargetingConditions: string[] = ["secondsOnSite", "secondsOnPage", "scrollPct", "exitIntent"]

const _evaluateTargetingForEvaluationResults = async (evaluationResults: TargetingEvaluationResult[]) => {
  const conditionSets: TargetingEvaluationResult[] = evaluationResults.filter(x => !x.allConditionsFullyEvaluated && x.allConditionsPotentiallyMatched)

  const evaluatableConditionSets = conditionSets
    .map(x => x.evaluatedTargetConditionSet)

  const evaluatableConditions = evaluatableConditionSets
    .map(conditionSet =>
        conditionSet
        .map(x => x.evaluatedTargetCondition)
        .map(x => x.condition)
    )

  const context = await generateTargetingContext()
  return await evaluateTargetingConditions({
    conditions: evaluatableConditions,
    context
  })
}

const _waitForCondition = async (asyncCondition: Record<string, any>, onCompletedEvaluation: Function = () => {}) => {
  const listenerId = uuid()

  const asyncConditionHandler = async (eventInfo: any = {}) => {
    const targetingContext = await generateTargetingContext()
    const conditionEvaluationResult = await asyncCondition.evaluator({ ...targetingContext, eventContext: eventInfo })

    if (!conditionEvaluationResult.conditionEvaluation) {
      return
    }

    if(!!asyncCondition.eventType) {

      // Mouse async events (only exitIntent currently) need to keep track of whether or not they're true in redux as the listener (and it's data) dies after a match
      if(asyncCondition.type === 'exitIntent') {
        store.dispatch(setHasExitIntent())
      }

      broadcast({
        topic: "remove-host-event-listener",
        message: {
          id: listenerId,
          event: asyncCondition.eventType
        }
      })

      onCompletedEvaluation()
    }
  }

  if(asyncCondition.timer) {
    const targetingContext = await generateTargetingContext()
    const { value: conditionValue } = asyncCondition.conditionDefinition
    const timeRemaining = conditionValue - asyncCondition.selector(targetingContext)

    if (timeRemaining > 0) {
      setTimeout(asyncConditionHandler, (timeRemaining * 1000 + 1000))
    }
  } else {
    broadcast({
      topic: "add-host-event-listener",
      message: {
        id: listenerId,
        event: asyncCondition.eventType,
        handler: asyncConditionHandler,
        eventProperties: asyncCondition.eventProperties || []
      }
    })
  }
}

export const evaluateConditionalSkillTargetingConditions = async (conditionalSkillTargetingResults: ConditionalSkillTargetingResult[]) : Promise<ConditionalSkillEvaluationPayload> => {
  if (!conditionalSkillTargetingResults || !conditionalSkillTargetingResults.length) {
    return null
  }

  const targetingRequestPayload: ConditionalSkillEvaluationPayload = {
    conditionGroups: [],
    ...getTargetingEvaluationMetadata()
  }

  for(const targetingResult of conditionalSkillTargetingResults) {
    if(targetingResult.status === TargetingEvaluationStatus.EVALUATED && targetingResult.matched) {
      targetingRequestPayload.conditionGroups.push(targetingResult)
    }
    else if (targetingResult.status === TargetingEvaluationStatus.PENDING) {
      const pendingConditionSets = targetingResult.conditionSets
        .filter(set => set.status === TargetingEvaluationStatus.PENDING)
        .map(set => set.targetingConditions)

      const evaluatableConditions = pendingConditionSets
        .map(set => set
          .filter(x => x.status === TargetingEvaluationStatus.PENDING)
          .map(x => x.evaluatedCondition))

      const evaluatedConditions = pendingConditionSets
      .map(set => set
        .filter(x => x.status === TargetingEvaluationStatus.EVALUATED))

      const flattenedEvaluatedConditions = flatten(evaluatedConditions)

      const context = await generateTargetingContext()
      const { matched, evalLog } =  await evaluateTargetingConditions({
        conditions: evaluatableConditions,
        context
      })

      if (flattenedEvaluatedConditions.length) {
        evalLog[0].targetingConditions = [...evalLog[0].targetingConditions, ...flattenedEvaluatedConditions]
      }

      targetingRequestPayload.conditionGroups.push({
        status: TargetingEvaluationStatus.EVALUATED,
        matched,
        conditionSets: evalLog,
        key: targetingResult.key,
        priority: targetingResult.priority
      })
    }
  }

  return targetingRequestPayload
}

export const evaluateRoutingTargetingConditions = async (routingTargetingResults: RoutingTargetingResult[]): Promise<MatchedRoutingRule[]> => {
  if (!routingTargetingResults || !routingTargetingResults.length) {
    return []
  }

  const matchedRuleIds: MatchedRoutingRule[] = []

  const toMatchedRule = (targetingResult: RoutingTargetingResult) => {
    return {
      id: targetingResult.ruleId,
      priority: targetingResult.priority
    }
  }

  for(const targetingResult of routingTargetingResults) {
    if(targetingResult.evaluationMatchOutcome === TargetingEvaluationOutcome.MATCH) {
      matchedRuleIds.push(toMatchedRule(targetingResult))
    }
    else if(targetingResult.evaluationMatchOutcome === TargetingEvaluationOutcome.POTENTIAL_MATCH) {
      const { matched } = await _evaluateTargetingForEvaluationResults(targetingResult.evaluationResults)

      if(matched) {
        matchedRuleIds.push(toMatchedRule(targetingResult))
      }
    }
  }

  return matchedRuleIds
}

export const evaluateWidgetDisplayTargetingConditions = async ({ evaluationMatchOutcome, evaluationResults = [], onMatched = () => {}, onDidNotMatch = () => {} }: EvaluateWidgetDisplayConditionsArgs) => {
  let failedConditionListeners = []

  if(evaluationMatchOutcome === TargetingEvaluationOutcome.MATCH) {
    return onMatched()
  }

  if(evaluationMatchOutcome === TargetingEvaluationOutcome.NO_MATCH) {
    return onDidNotMatch()
  }

  if(evaluationMatchOutcome === TargetingEvaluationOutcome.POTENTIAL_MATCH) {
    const { matched, asyncConditionListeners } = await _evaluateTargetingForEvaluationResults(evaluationResults)

    if(matched) {
      return onMatched()
    } else {
      failedConditionListeners = [...failedConditionListeners, ...asyncConditionListeners]
    }
  }

  failedConditionListeners.forEach(conditionListener => _waitForCondition(conditionListener, () => {
    return onMatched()
  }))

  if(!failedConditionListeners.length) {
    return onDidNotMatch()
  }

  return onDidNotMatch()
}

export const evaluateCampaignTargetingConditions = async ({ conditions = [], onMatched = () => {}, onDidNotMatch = () => {} }: EvaluateCampaignTargetingConditionsArgs ) => {
  const targetingAuditLog : TargetingAuditLog = {
    conditionGroups: []
  }

  let failedConditionListeners = []

  const onConditionMatched = async ({ campaignId, matchedConditions = [] }: OnTargetingConditionsMatchedArgs) => {
    const matchedAsyncCondition: boolean = matchedConditions
      .map(condition => condition.field)
      .filter(field => asyncTargetingConditions.includes(field))
      .length > 0

    await SaveAuditLog(targetingAuditLog)

    onMatched && onMatched(campaignId, matchedAsyncCondition)
    return Promise.resolve({
      TAL: targetingAuditLog,
      matched: true
    })
  }

  const onNoConditionMatched = async () => {
    await SaveAuditLog(targetingAuditLog)
    onDidNotMatch && onDidNotMatch()

    return Promise.resolve({
      TAL: targetingAuditLog,
      matched: false
    })
  }

  function getMatchedConditionFromEvalLog(evalLog: TargetingAuditLogConditionSet[]) {
    const matchedConditions: (TargetingAuditLogCondition[])[] = evalLog.filter(log => log.status === 'EVALUATED')
      .map(log => log.targetingConditions)
      .filter(targetingCondition => targetingCondition.filter(condition => condition.matched))
    return chain(matchedCondition => matchedCondition.map(condition => condition.evaluatedCondition), matchedConditions) as TargetingCondition[]
  }

  function getMatchedConditionsFromEvalResults(evaluationResults: TargetingEvaluationResult[]) {
    const evaluatedConditionSets: EvaluatedTargetConditionSetItem[] = chain(result => result.evaluatedTargetConditionSet, evaluationResults.filter(result => result.fullyEvaluatedAndMatched))
    return evaluatedConditionSets
      .filter(conditionSet => conditionSet.fullyEvaluated)
      .map(conditionSet => conditionSet.evaluatedTargetCondition)
      .filter(targetingCondition => targetingCondition.matched)
      .map(matchedCondition => matchedCondition.condition)
  }

  for(const [index,targetingResult] of conditions.entries()) {
    const { campaignId, evaluationMatchOutcome, evaluationResults = [] } = targetingResult

    if(evaluationMatchOutcome === TargetingEvaluationOutcome.NO_MATCH) {
      AddEvaluationResultToAuditLog({
        evaluationResults,
        auditLog: targetingAuditLog,
        didMatch: false,
        campaignId: campaignId,
        priority: index + 1
      })
    }
    else if(evaluationMatchOutcome === TargetingEvaluationOutcome.MATCH) {
      AddEvaluationResultToAuditLog({
        evaluationResults,
        auditLog: targetingAuditLog,
        didMatch: true,
        campaignId: campaignId,
        priority: index + 1
      })

      const matchedConditionsFromEvalResults: TargetingCondition[] = getMatchedConditionsFromEvalResults(evaluationResults)
      return onConditionMatched({ campaignId, matchedConditions: matchedConditionsFromEvalResults })
    }
    else if(evaluationMatchOutcome === TargetingEvaluationOutcome.POTENTIAL_MATCH) {
      const {
        matched,
        asyncConditionListeners,
        evalLog = []
      } = await _evaluateTargetingForEvaluationResults(evaluationResults)

      AddEvaluationResultToAuditLog({
        auditLog: targetingAuditLog,
        evaluationResults: evalLog,
        didMatch: matched,
        campaignId,
        priority: index + 1,
        isServerGenerated: false,
        status: asyncConditionListeners.length ? TargetingEvaluationStatus.PENDING : TargetingEvaluationStatus.EVALUATED,
      })

      if(matched) {
        const matchedConditionFromEvalLog: TargetingCondition[] = getMatchedConditionFromEvalLog(evalLog)
        return onConditionMatched({ campaignId, matchedConditions: matchedConditionFromEvalLog })
      } else {
        failedConditionListeners = [...failedConditionListeners, ...asyncConditionListeners]
      }
    }

    if(index === conditions.length -1) {
      failedConditionListeners.forEach(conditionListener => _waitForCondition(conditionListener, () => {
        return evaluateCampaignTargetingConditions({ conditions, onMatched, onDidNotMatch })
      }))

      if(!failedConditionListeners.length) {
        return onNoConditionMatched()
      }
    }
  }

  return onNoConditionMatched()
}
